import React, { useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { zIndex } from '@ecp/common/src/style/constant';
import boTheme from '@ecp/common/src/style/theme/bo';
import { boScrollCss } from '@ecp/common/src/style/theme/defaultStyled';
import { FlexBox, FlexBoxColumn, FlexBoxSpaceBetween } from '@ecp/common/src/layouts/flex/styled';
import { TextBox } from '@ecp/common/src/text/TextBox';
import DrawerUnstyled from '@ecp/common/src/components/unstyled/floating/DrawerUnstyled';
import { ReactComponent as CloseIcon } from '@ecp/common/src/assets/icon/icon__close_drawer.svg';

const Drawer = ({ open, onClose, title, children, duration = 500, delay = '50ms', ...props }) => {
  return (
    <DrawerUnstyled
      {...props}
      placement={'right'}
      closeWhenOutside={false}
      rootComponent={DrawerRoot}
      open={open}
      onClose={onClose}
      title={title}
      value={<FlexBoxColumn width={'100%'}>{children}</FlexBoxColumn>}
      duration={duration}
      delay={delay}
    />
  );
};

Drawer.displayName = 'Drawer';

Drawer.propTypes = {
  title: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element, PropTypes.node]),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  offset: PropTypes.number,
  duration: PropTypes.number,
  delay: PropTypes.string,
};

const DrawerRoot = React.forwardRef(({ title, onClose, children, ...props }, ref) => {
  const rootRef = useRef(null);
  useImperativeHandle(ref, () => rootRef.current);

  return (
    <DrawerOverlay>
      <DrawerWrapper ref={rootRef} {...props}>
        <DrawerHeader>
          <TextBox size={boTheme.font.size.title} weight={boTheme.font.weight.bold} height={'36px'}>
            {title}
          </TextBox>
          <DrawerClose onClick={onClose} />
        </DrawerHeader>
        <DrawerBody>{children}</DrawerBody>
      </DrawerWrapper>
    </DrawerOverlay>
  );
});

DrawerRoot.displayName = 'StyledDrawerRoot';

DrawerRoot.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
  onClose: PropTypes.func,
};

DrawerRoot.defaultProps = {
  title: '',
};

const DrawerOverlay = styled(FlexBox)`
  position: absolute;
  width: max-content;
  height: calc(100% - 50px);
  overflow: hidden;
  z-index: ${zIndex.modal};
  top: 50px;
  right: 0;
  margin-left: -10px;
  padding-left: 10px;
`;

const DrawerWrapper = styled(FlexBoxColumn)`
  position: relative !important;
  align-items: flex-start;
  width: 540px;
  height: 100%;
  background-color: ${boTheme.color.sub10};
  padding-bottom: 30px;
  box-shadow: 2px 4px 10px rgba(0, 0, 0, 0.15);

  &:focus-visible {
    outline: none;
  }
`;

const DrawerHeader = styled(FlexBoxSpaceBetween)`
  align-items: flex-end;
  height: 56px;
  width: 100%;
  padding: 0 20px;
`;

const DrawerBody = styled(FlexBox)`
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  height: calc(100% - 56px);
  padding: 0 20px;
  overflow-y: auto;
  overflow-x: hidden;
  flex-wrap: nowrap;

  ${boScrollCss};
`;

const DrawerClose = styled(CloseIcon)`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;

  background: ${boTheme.color.sub8};
`;

export default Drawer;
